import type { Branded, Nullable } from '@cosmos/types-common';

// Cached canvas and context for performance optimization
let cachedCanvas: HTMLCanvasElement | null = null;
let cachedContext: CanvasRenderingContext2D | null = null;
const seen = new Map<string, NormalizedHex[]>();
const TRANSPARENT = 'transparent' as NormalizedHex;

export type NormalizedHexId = Branded<string, 'NormalizedHexId'>;
export type NormalizedHex = Branded<string, 'NormalizedHex'>;

/**
 * Enhanced hex normalization that handles multiple formats and color names
 * Returns an array of normalized hex codes or "transparent" for invalid values
 *
 * Handles:
 * - Multiple hex values separated by commas or semicolons
 * - Hex values with or without hash prefix (both 6-digit and 3-digit shorthand)
 * - Color words using browser CSS parsing capabilities
 * - Invalid values return "transparent" instead of being filtered out
 *
 * @param input - The color string to parse
 * @param _document - Optional document object (for Angular environments)
 */
export function normalizeHex(
  input: Nullable<string>,
  _document: Document
): NormalizedHex[] {
  if (!input) return [];

  if (seen.has(input)) return [...seen.get(input)!];

  const trimmed = input.trim();
  if (!trimmed) return [];

  // Split by commas or semicolons to handle multiple values
  const parts = trimmed
    .split(/[,;]/)
    .map((part) => part.trim())
    .filter((part) => part);
  const results: NormalizedHex[] = [];

  for (const part of parts) {
    const normalized = normalizeSingleValue(part, _document) || TRANSPARENT;
    if (results.includes(normalized)) {
      // avoid adding duplicated values
      continue;
    }
    results.push(normalized);
  }

  seen.set(input, [...results]);

  return results;
}

export function getNormalizedHexId(hexes: string[]): NormalizedHexId {
  return hexes.join(';') as NormalizedHexId;
}

/** Normalize a single hex code to #RRGGBB uppercase or return '' when invalid. Supports both 6-digit and 3-digit shorthand formats. */
function normalizeSingleHex(hex?: string | null): NormalizedHex | null {
  if (!hex) return null;
  let h = hex.trim();

  // Handle 6-digit hex (with or without #)
  if (/^[0-9a-fA-F]{6}$/.test(h)) h = `#${h}`;
  if (/^#[0-9a-fA-F]{6}$/.test(h)) return h.toUpperCase() as NormalizedHex;

  // Handle 3-digit hex shorthand (with or without #)
  if (/^[0-9a-fA-F]{3}$/.test(h)) h = `#${h}`;
  if (/^#[0-9a-fA-F]{3}$/.test(h)) {
    // Expand shorthand: #abc -> #aabbcc
    const expanded =
      h.charAt(1) +
      h.charAt(1) +
      h.charAt(2) +
      h.charAt(2) +
      h.charAt(3) +
      h.charAt(3);
    return `#${expanded.toUpperCase()}` as NormalizedHex;
  }

  return null;
}

/**
 * Convert a CSS color (name, rgb, hsl, etc.) to hex using browser capabilities
 */
function cssColorToHex(color: string, doc?: Document): NormalizedHex | null {
  try {
    // Use provided document parameter - caller should inject DOCUMENT token in Angular
    if (!doc) return null;

    // Initialize cached canvas and context if needed
    if (!cachedCanvas || !cachedContext) {
      cachedCanvas = doc.createElement('canvas');
      // Set minimal size since we don't actually draw anything
      cachedCanvas.width = 1;
      cachedCanvas.height = 1;
      cachedContext = cachedCanvas.getContext('2d');

      if (!cachedContext) {
        return null;
      }
    }

    // Set the color and read it back - browser will normalize it to rgba format
    cachedContext.fillStyle = color;
    const normalizedColor = cachedContext.fillStyle;

    // If the color didn't change from default or is invalid, return null
    if (
      !normalizedColor ||
      (normalizedColor === '#000000' &&
        color.toLowerCase() !== 'black' &&
        color !== '#000000')
    ) {
      return null;
    }

    // If it's already a hex color, return it normalized
    if (normalizedColor.startsWith('#')) {
      return normalizedColor.length === 7
        ? (normalizedColor.toUpperCase() as NormalizedHex)
        : null;
    }

    // Parse RGB values from the normalized color (format: "rgb(r, g, b)" or "rgba(r, g, b, a)")
    const rgbMatch = normalizedColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (!rgbMatch) return null;

    const r = parseInt(rgbMatch[1], 10);
    const g = parseInt(rgbMatch[2], 10);
    const b = parseInt(rgbMatch[3], 10);

    // Convert RGB to hex
    const toHex = (n: number) => n.toString(16).padStart(2, '0').toUpperCase();
    return `#${toHex(r)}${toHex(g)}${toHex(b)}` as NormalizedHex;
  } catch {
    return null;
  }
}

/**
 * Normalize a single color value (hex or color name)
 */
function normalizeSingleValue(
  value: string,
  _document?: Document
): NormalizedHex | null {
  if (!value) return null;

  const trimmed = value.trim();

  // Try to normalize as hex first
  const hexResult = normalizeSingleHex(trimmed);
  if (hexResult) return hexResult;

  // Try to parse as CSS color using browser capabilities
  const cssResult = cssColorToHex(trimmed, _document);
  if (cssResult) return cssResult;

  const lowerValue = trimmed.toLowerCase();

  // Try partial matching - check if any known color names are contained in the input
  const commonColors = [
    'red',
    'green',
    'blue',
    'yellow',
    'orange',
    'purple',
    'pink',
    'brown',
    'black',
    'white',
    'gray',
    'grey',
    'silver',
    'gold',
    'navy',
    'maroon',
    'lime',
    'olive',
    'aqua',
    'teal',
    'fuchsia',
    'cyan',
    'magenta',
    'coral',
    'salmon',
    'khaki',
    'violet',
    'indigo',
    'turquoise',
    'beige',
    'ivory',
    'lavender',
    'mint',
    'peach',
  ];

  for (const color of commonColors) {
    // look for the specific color word
    if (new RegExp(`\\b${color}\\b`, 'i').test(lowerValue)) {
      const partialResult = cssColorToHex(color, _document);
      if (partialResult) return partialResult;
    }
  }

  return null;
}
